import { gql, useMutation, useQuery } from '@apollo/client'
import numeral from 'numeral'
import { useMemo, useState } from 'react'
import { generatePath } from 'react-router-dom'

import {
  PaymentDetailFragmentDoc,
  PayPreSaleLotteryMutation,
  PayPreSaleLotteryMutationVariables,
  PreSaleLotteryApplicantConfirmationFragmentDoc,
  PreSaleLotteryApplicantQuery,
  PreSaleLotteryApplicantQueryVariables,
} from '@/clients/utoniq-core/schema'
import { constants } from '@/config'
import { useMessage, useRouter, useTranslation } from '@/core'
import { makeViewModel } from '@/core/architecture'
import { createUuid } from '@/lib/uuid'

import { Action, State } from './template'

/**
 *
 */
export const useViewModel = makeViewModel<State, Action>(() => {
  const { router, routerPages } = useRouter<{ preSaleId: string }>()
  const { t } = useTranslation('lotteryApplicationDetailPaymentConfirmation')
  const { showError } = useMessage()
  const { data, loading } = useQuery<
    PreSaleLotteryApplicantQuery,
    PreSaleLotteryApplicantQueryVariables
  >(QUERY, {
    variables: {
      preSaleId: router.query.preSaleId,
    },
  })

  const [payPreSaleLottery, { loading: submitting }] = useMutation<
    PayPreSaleLotteryMutation,
    PayPreSaleLotteryMutationVariables
  >(PAY_PRE_SALE_LOTTERY)

  const { choiceItemDetails, winChoiceNumber } =
    data?.preSaleLotteryApplicant || {}
  const winItem = useMemo(
    () => choiceItemDetails?.[(winChoiceNumber ?? 0) - 1],
    [winChoiceNumber, choiceItemDetails]
  )
  const ticketPrice = useMemo(
    () => (winItem?.skuUnit?.price || winItem?.item?.price) ?? 0,
    [winItem]
  )
  const fees = winItem?.fees
  const billingStatements = useMemo(
    () => [
      {
        label: t('ticketPrice'),
        value: `￥${numeral(ticketPrice * (winItem?.count ?? 0)).format(
          '0,0'
        )}`,
      },
      ...(fees?.collections || []).map(fee => ({
        label: fee?.feeName,
        value: `￥${numeral(fee?.fee).format('0,0')}`,
      })),
      // @todo コンビニ決済、配送アイテム対応時に手数料を追加する
    ],
    [t, fees, ticketPrice]
  )
  const totalPayment = useMemo(() => {
    const konbiniFee = fees?.konbini?.fee ?? 0
    const shippingFee = fees?.shipping?.fee ?? 0
    const collectionsFee =
      fees?.collections?.reduce((acc, fee) => acc + (fee?.fee ?? 0), 0) ?? 0
    return (
      konbiniFee +
      shippingFee +
      collectionsFee +
      ticketPrice * (winItem?.count ?? 0)
    )
  }, [fees, ticketPrice])

  const [openExpiredCardModal, setOpenExpiredCardModal] = useState(false)
  const [openPaymentErrorModal, setOpenPaymentErrorModal] = useState(false)
  const [openRequire3DSecureModal, setOpenRequire3DSecureModal] =
    useState(false)

  return {
    state: {
      loading,
      cardNumber: data?.paymentSource?.last4,
      brand: data?.paymentSource?.brand ?? '',
      submitting,
      totalPayment,
      billingStatements,
      openExpiredCardModal,
      openPaymentErrorModal,
      openRequire3DSecureModal,
    },
    actions: {
      onPurchase: async () => {
        return payPreSaleLottery({
          variables: {
            inputs: {
              preSaleId: router.query.preSaleId,
              price: totalPayment,
            },
          },
          context: {
            headers: { 'x-utoniq-idempotency-key': createUuid() },
          },
        })
          .then(order => {
            // StoreItemPurchaseComplete に遷移
            router.push(
              generatePath(routerPages.StoreItemPurchaseComplete, {
                cartTransactionOrderId: order?.data?.payPreSaleLottery?.id,
              })
            )
          })
          .catch(error => {
            switch (error.graphQLErrors?.[0]?.extensions?.errorCode) {
              /**
               * @description
               * 3Dセキュア認証が必要な場合
               */
              case constants.errorCodes.REQUIRE_3D_SECURE:
                setOpenRequire3DSecureModal(true)
                return
              /**
               * @desciption
               * カードが有効期限切れの場合
               */
              case constants.errorCodes.EXPIRED_CARD:
                setOpenExpiredCardModal(true)
                return
              /**
               * @desciption
               * 何かしらの決済エラーの場合
               */
              case constants.errorCodes.PAYMENT_ERROR:
                setOpenPaymentErrorModal(true)
                return
              default:
                showError(error)
                break
            }
          })
      },
      onCloseRequire3DSecureModal() {
        setOpenRequire3DSecureModal(false)
      },
      onCloseExpiredCardModal() {
        setOpenExpiredCardModal(false)
      },
      onClosePaymentErrorModal() {
        setOpenPaymentErrorModal(false)
      },
    },
  }
})

const QUERY = gql`
  query LotteryApplicationDetailPaymentPaymentConfirmation(
    $preSaleId: String!
  ) {
    preSaleLotteryApplicant(preSaleId: $preSaleId) {
      ...PreSaleLotteryApplicantConfirmation
    }
    paymentSource {
      ...PaymentDetail
    }
  }
  ${PreSaleLotteryApplicantConfirmationFragmentDoc}
  ${PaymentDetailFragmentDoc}
`

const PAY_PRE_SALE_LOTTERY = gql`
  mutation payPreSaleLottery($inputs: PayPreSaleLotteryInput!) {
    payPreSaleLottery(inputs: $inputs) {
      id
    }
  }
`
